import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Popper, { PopperPlacementType } from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import "./LogFilters.css";
import { useLocation, useNavigate } from "react-router-dom";
interface props {
  logs: any;
  setSelectedStatus: any;
  selectedStatus: any;
  setSelectedMethod: any;
  selectedMethod: any;
  setSelectedLastRun: any;
  selectedLastRun: any;
  setRowID: any;
  setProcessId: any;
  setSelectedName: any;
  setPage: any;
  setIsClearFilter: any;
  setFilteredLogs: any;
  isFilterApplied: any;
  setRefresh: any;
}
export default function LogFiltersItems({
  selectedStatus,
  setSelectedStatus,
  setSelectedMethod,
  selectedMethod,
  setSelectedLastRun,
  selectedLastRun,
  setRowID,
  setProcessId,
  setSelectedName,
  setPage,
  setIsClearFilter,
  isFilterApplied,
  setRefresh,
}: props) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [open, setOpen] = React.useState(false);
  const [placement, setPlacement] =
    React.useState<PopperPlacementType>("bottom-start"); // Set default placement
  const [selectedContentIndex, setSelectedContentIndex] =
    React.useState<number>(0);
  const uniqueStatuses = [
    "SUCCESS",
    "FAILURE",
    "PENDING",
    "RUNNING",
    "WARNING",
    "CANCELLED",
    "QUEUED",
  ];
  const uniqueMethods = ["MANUAL", "SYSTEM"];
  const location = useLocation();
  const navigate = useNavigate();
  const handleDateChange = (dateRange: DateRange<Dayjs>) => {
    // Always use the dateRange directly, preserving the Dayjs objects
    setSelectedLastRun(dateRange);
    const newSearchParams = new URLSearchParams(location.search);
    if (dateRange[0] && dateRange[1]) {
      // Only add to URL if both dates are selected

      newSearchParams.set( 
        "startDate",
        dayjs(dateRange[0]).format("YYYY-MM-DD")
      );
      setRowID(null);
      newSearchParams.set("endDate", dayjs(dateRange[1]).format("YYYY-MM-DD"));
      newSearchParams.delete("run_id");
      setPage(0);
      newSearchParams.set("page", "0");
    } else {
      newSearchParams.delete("startDate");
      newSearchParams.delete("endDate");
    }
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };
  const [clicked, setClicked] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const handleStatusOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setSelectedStatus((prevSelected: any[] = []) => {
      const newSelected =
        prevSelected !== undefined && prevSelected.includes(value)
          ? prevSelected.filter((item: any) => item !== value)
          : [...prevSelected, value];
      const newSearchParams = new URLSearchParams(location.search);
      if (newSelected.length > 0) {
        setRowID(null);
        newSearchParams.set("status", newSelected.join(","));
        newSearchParams.delete("run_id");
        setPage(0);
        newSearchParams.set("page", "0");
      } else {
        newSearchParams.delete("status");
      }
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
      return newSelected;
    });
    // setStatusSelected(true);
  };
  const handleMethodOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setSelectedMethod((prevSelected: any[] = []) => {
      const newSelected =
        prevSelected !== undefined && prevSelected.includes(value)
          ? prevSelected.filter((item: any) => item !== value)
          : [...prevSelected, value];
      const newSearchParams = new URLSearchParams(location.search);
      if (newSelected.length > 0) {
        setRowID(null);
        newSearchParams.set("method", newSelected.join(","));
        newSearchParams.delete("run_id");
        setPage(0);
        newSearchParams.set("page", "0");
      } else {
        newSearchParams.delete("method");
      }
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
      return newSelected;
    });
    // setStatusSelected(true);
  };
  const handleClearFilter = () => {
    setSelectedStatus([]);
    setSelectedMethod([]);
    setSelectedLastRun([]);
    setRowID(null);
    setProcessId(null);
    setSelectedName(null);
    setPage(0);
    setIsClearFilter(true);
    setRefresh(true);
    // setFilteredLogs([]);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete("status");
    newSearchParams.delete("method");
    newSearchParams.delete("startDate");
    newSearchParams.delete("endDate");
    newSearchParams.delete("search");
    newSearchParams.delete("page");
    newSearchParams.delete("run_id");
    newSearchParams.delete("response_run_id");
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };
  useEffect(() => {
    // Only run this effect if selectedStatus or selectedMethod has values
    if (!open && (selectedStatus?.length > 0 || selectedMethod?.length > 0)) {
      setSelectedStatus((prevSelected: any[] = []) => [...prevSelected]);
      setSelectedMethod((prevSelected: any[] = []) => [...prevSelected]);
    }
    // eslint-disable-next-line
  }, []); // Add selectedStatus and selectedMethod to dependencies

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: "This Week",
      getValue: () => {
        const today = dayjs();
        return [today.startOf("week"), today.endOf("week")];
      },
    },
    {
      label: "Last Week",
      getValue: () => {
        const today = dayjs();
        const prevWeek = today.subtract(7, "day");
        return [prevWeek.startOf("week"), prevWeek.endOf("week")];
      },
    },
    {
      label: "Last 7 Days",
      getValue: () => {
        const today = dayjs();
        return [today.subtract(7, "day"), today];
      },
    },
    {
      label: "Current Month",
      getValue: () => {
        const today = dayjs();
        return [today.startOf("month"), today.endOf("month")];
      },
    },
    {
      label: "Next Month",
      getValue: () => {
        const today = dayjs();
        const startOfNextMonth = today.endOf("month").add(1, "day");
        return [startOfNextMonth, startOfNextMonth.endOf("month")];
      },
    },
    { label: "Reset", getValue: () => [null, null] },
  ];
  const content = [
    <LocalizationProvider dateAdapter={AdapterDayjs} >
      <StaticDateRangePicker
        slotProps={{
          shortcuts: {
            items: shortcutsItems,
          },
          actionBar: { actions: [] },
        }}
        calendars={2}
        value={selectedLastRun}
        onChange={handleDateChange}
      />
    </LocalizationProvider>,
    <div className="tabDiv">
      <Typography variant="body2" sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}>
        Select Status
      </Typography>
      <FormGroup>
        {(uniqueStatuses as string[]).map((status: string, index: number) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={
                  selectedStatus !== undefined &&
                  selectedStatus.includes(status)
                }
                onChange={handleStatusOptionChange}
                value={status}
                color="secondary"
              />
            }
            label={status} // Display the status
          />
        ))}
      </FormGroup>
    </div>,
    <div className="tabDiv">
      <Typography variant="body2" sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}>
        Select a Method
      </Typography>
      <RadioGroup>
        {(uniqueMethods as string[]).map((method: string, index: number) => (
          <FormControlLabel
            key={index}
            // checked={selectedMethod.includes(method)}
            // value={method}
            control={
              <Checkbox
                checked={
                  selectedMethod !== undefined &&
                  selectedMethod.includes(method)
                }
                onChange={handleMethodOptionChange}
                value={method}
                color="secondary"
              />
            }
            label={method} // Display the method as the label
          />
        ))}
      </RadioGroup>
    </div>,
  ];
  const handleClick =
    (newPlacement: PopperPlacementType, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (anchorEl === event.currentTarget && open) {
        setOpen(false);
        setClicked([false, false, false, false]);
      } else {
        setAnchorEl(event.currentTarget);
        setOpen(true);
        setPlacement(newPlacement);
        setSelectedContentIndex(index);
        setClicked((prevClicked) => prevClicked.map((_, i) => i === index));
      }
    };
  const popperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !anchorEl?.contains(event.target as Node)
      ) {
        setOpen(false);
        setClicked([false, false, false, false]);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorEl]);
  return (
    <Box data-tracker="223" sx={{ width: 500 }}>
      <Popper
        sx={{ zIndex: 1200 }}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper data-tracker="223" ref={popperRef}>{content[selectedContentIndex]}</Paper>
          </Fade>
        )}
      </Popper>
      {["Start Date", "Status", "Method"].map((letter, index) => (
        <Button
          sx={{ "&:hover": { backgroundColor: "rgba(156, 39, 176, 0.04)" } }}
          key={index}
          onClick={handleClick("bottom-start", index)}
        >
          <span
            style={
              (index === 1 && selectedStatus && selectedStatus?.length >= 1) ||
              (index === 2 && selectedMethod && selectedMethod?.length >= 1) ||
              (index === 0 &&
                selectedLastRun &&
                selectedLastRun[0] &&
                selectedLastRun[1])
                ? { color: "#9C27B0" }
                : { color: clicked[index] ? "#9C2780" : "#000000DE" }
            }
          >
            {letter}
          </span>
          {/* Added span tag to display the count of selected options */}
          {index === 1 && selectedStatus && selectedStatus.length >= 1 ? (
            <span style={{ color: "#9C27B0" }}>+{selectedStatus?.length}</span>
          ) : index === 2 && selectedMethod && selectedMethod.length >= 1 ? (
            <span
              style={{ color: "#9C27B0" }}
            >{`+${selectedMethod?.length}`}</span>
          ) : index === 0 &&
            selectedLastRun &&
            selectedLastRun[0] &&
            selectedLastRun[1] ? (
            <span style={{ color: "#9C27B0" }}>{`+${
              selectedLastRun?.length - 1
            }`}</span>
          ) : (
            <KeyboardArrowDownIcon
              style={
                (index === 1 && selectedStatus?.length >= 1) ||
                (index === 2 && selectedMethod?.length >= 1) ||
                (index === 0 &&
                  selectedLastRun &&
                  selectedLastRun[0] &&
                  selectedLastRun[1])
                  ? { color: "#9C27B0" }
                  : { color: clicked[index] ? "#9C2780" : "#000000DE" }
              }
            />
          )}
        </Button>
      ))}
      <Button
        sx={{
          textTransform: "capitalize",
          fontWeight: 500,
          color: "#000000DE",
          "&:hover": {
            color: "#9C27B0",
            backgroundColor: "rgba(156, 39, 176, 0.04)",
          },
        }}
        onClick={handleClearFilter}
        disabled={!isFilterApplied()}
      >
        Clear Filter
      </Button>
    </Box>
  );
}
